<i18n>
{
  "en": {
    "reportingTitle": "Review your reports",
    "periodInfo": "Available information for period",
    "periodInfoDates": "from {startDate} to {endDate}",
    "isManagedLabel": "Revenue managed active",
    "dinerGroupLabel": "Information by diner",
    "globalGroupLabel": "Global information",
    "productTableLabel": "Sales information"
  },
  "es": {
    "reportingTitle": "Revisa tu progreso con nuestros informes",
    "periodInfo": "Información disponible para el periodo desde",
    "periodInfoDates": "el {startDate} hasta {endDate}",
    "isManagedLabel": "Gestión de ingresos activa",
    "dinerGroupLabel": "Datos por comensal",
    "globalGroupLabel": "Información global",
    "productTableLabel": "Información de ventas"
  }
}
</i18n>

<template>
  <v-container class="pb-8 d-flex flex-wrap flex-column container">
    <v-sheet class="text-center text-h6">{{ $t('reportingTitle') }}</v-sheet>
    <v-sheet class="text-center mt-2 body-2">{{ $t('periodInfo') }}</v-sheet>
    <v-sheet class="text-center body-2">{{ periodInfoLabel }}</v-sheet>
    <v-sheet class="d-flex align-center justify-center ml-2 ml-md-0">
      <v-switch v-model="isManaged" :label="$t('isManagedLabel')" color="success"
                class="justify-center mr-3 mr-md-10" dense hide-details
                prepend-icon="mdi-currency-usd-circle-outline"/>
    </v-sheet>
    <v-card-actions v-if="!isLoading" class="justify-space-between justify-md-center pa-0 mt-4">
      <v-btn text @click="prev" class="mr-md-15">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <v-item-group v-model="currentKpiGroup" class="text-center" mandatory>
        <v-item v-for="n in kpiGroupList.length" :key="`btn-${n}`" v-slot="{ active, toggle }">
          <v-btn :input-value="active" icon @click="toggle">
            <v-icon>mdi-record</v-icon>
          </v-btn>
        </v-item>
      </v-item-group>
      <v-btn text @click="next" class="ml-md-15">
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </v-card-actions>

    <v-window v-if="!isLoading" continuous class="carousel-container" v-model="currentKpiGroup">
      <v-window-item v-for="{label, kpis} in kpiGroupList" :key="label" eager>
        <v-sheet class="pa-1 d-flex flex-column align-center flex-grow-1">
          <span class="body-1 mt-md-10 mb-md-4 mt-4 mb-3">{{ $t(label) }}</span>
          <component v-for="kpiComponent in kpis" :key="kpiComponent" :is="kpiComponent" :is-managed="isManaged"/>
        </v-sheet>
      </v-window-item>
    </v-window>
  </v-container>
</template>

<script>
import Component from 'vue-class-component';
import Vue from 'vue';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import DynameatSkeleton from '@dynameat/components/DynameatSkeleton';
import { UncubesHTTPController } from '@dynameat/views/reporting/models/uncubesHTTPController';
import { uncubesServerTokenRequest } from '@dynameat/views/reporting/api';
import { sentryLogException } from '@core/helpers/sentryLogger';
import RevenueKpi from './components/RevenueKpi.vue';
import OccupationKpi from './components/OccupationKpi.vue';
import DinerMarginKpi from './components/DinerMarginKpi.vue';
import DinerSpendingKpi from './components/DinerSpendingKpi.vue';
import ProductSalesTable from './components/ProductSalesTable.vue';
import RevPashKpi from './components/RevPashKpi.vue';
import AverageDinerTimeKpi from './components/AverageDinerTimeKpi.vue';
import AverageTicketKpi from './components/AverageTicketKpi.vue';
import ProfitChart from './components/ProfitChart.vue';
import MarginKpi from './components/MarginKpi.vue';

@Component
@Components({
  RevenueKpi,
  MarginKpi,
  OccupationKpi,
  DinerMarginKpi,
  DinerSpendingKpi,
  ProductSalesTable,
  RevPashKpi,
  AverageDinerTimeKpi,
  AverageTicketKpi,
  ProfitChart,
  DynameatSkeleton
})
@MapActions(['setUncubesControllerReady'])
@MapGetters(['lastTwoPastPeriods', 'uncubesServerReady', 'isLoading'])
export default class Reporting extends Vue {
  isManaged = false;

  currentKpiGroup = 0

  get kpiGroupList() {
    return [
      {
        label: 'globalGroupLabel',
        kpis: ['profit-chart', 'revenue-kpi', 'margin-kpi', 'occupation-kpi', 'rev-pash-kpi', 'average-ticket-kpi']
      },
      { label: 'dinerGroupLabel', kpis: ['diner-spending-kpi', 'diner-margin-kpi', 'average-diner-time-kpi'] },
      { label: 'productTableLabel', kpis: ['product-sales-table'] },
    ];
  }

  get periodInfoLabel() {
    const period = this.lastTwoPastPeriods[0];
    const startDate = period?.startDateLocaleStr || '';
    const endDate = period?.endDateLocaleStr || '';
    return this.$t('periodInfoDates', { startDate, endDate });
  }

  async created() {
    await this.initializeReportingClient();
  }

  async initializeReportingClient() {
    try {
      if (!this.uncubesServerReady) {
        const tokenRequest = await uncubesServerTokenRequest();
        const token = tokenRequest.data;
        UncubesHTTPController.initialize(token);
        await this.setUncubesControllerReady();
      }
    } catch (err) {
      sentryLogException(err, 'Error initializing Uncubes Server Controller');
    }
  }

  next() {
    this.currentKpiGroup = this.currentKpiGroup + 1 === this.kpiGroupList.length ? 0 : this.currentKpiGroup + 1;
  }

  prev() {
    this.currentKpiGroup = this.currentKpiGroup - 1 < 0 ? this.kpiGroupList.length - 1 : this.currentKpiGroup - 1;
  }

  get skeletonClass() {
    return this.$vuetify.breakpoint.mdAndUp ? 'half-width' : 'full-width';
  }
}
</script>

<style scoped lang="scss">
.container {
  height: 100%;
}

.carousel-container {
  display: flex;
  flex: 1;

  > :first-child {
    display: flex;
    flex: 1;

    > div {
      display: flex;
      flex: 1;
    }
  }
}
</style>
